/**
 * Sanitizes sample JSON for design-time schema use and manages schema metadata for the
 * V3 From JSON Object - Set Schema block.
 *
 * Sanitization rules:
 * - Strings become "", numbers become 0, and booleans become false.
 * - Null, unsupported values, and conflicting concrete types become null.
 * - Objects keep their properties and are sanitized recursively.
 * - Arrays keep one representative item; object properties are combined recursively.
 * - Empty arrays remain empty, and null plus a concrete array-item type uses the concrete type.
 * - Values are sanitized by their JSON type, so date-formatted strings also become "".
 *
 * Schemas are versioned and stored by block ID in workspace.blockSchemas. The block XML
 * stores only the corresponding schema reference.
 */
export const BLOCK_SCHEMA_VERSION = 1; // Version of the stored schema metadata format, independent of the Blockly block version.
export const FROM_JSON_WITH_SCHEMA_BLOCK_TYPE = 'get_from_json_with_schema_V3';

// Convert a sample value to an intermediate node so null/unknown and conflicting types remain distinguishable while merging arrays.
function toSchemaNode(value) {
    if (value === null) return { kind: 'unknown' };

    switch (typeof value) {
        case 'string':
            return { kind: 'string' };
        case 'boolean':
            return { kind: 'boolean' };
        case 'number':
            return { kind: 'number' };
        case 'object':
            if (Array.isArray(value)) {
                if (value.length === 0) return { kind: 'array', hasItem: false };

                const item = value
                    .map(toSchemaNode)
                    .reduce((merged, current) => merged === undefined ? current : mergeSchemaNodes(merged, current), undefined);

                return { kind: 'array', hasItem: true, item };
            }

            return {
                kind: 'object',
                properties: Object.entries(value).reduce((properties, [key, propertyValue]) => {
                    properties[key] = toSchemaNode(propertyValue);
                    return properties;
                }, {}),
            };
        default:
            return { kind: 'unknown' };
    }
}

// Combine two array-item schema nodes, recursively merging object properties and marking incompatible concrete types as conflicts.
function mergeSchemaNodes(left, right) {
    if (left.kind === 'conflict' || right.kind === 'conflict') return { kind: 'conflict' };
    if (left.kind === 'unknown') return right;
    if (right.kind === 'unknown') return left;
    if (left.kind !== right.kind) return { kind: 'conflict' };

    if (left.kind === 'object') {
        const properties = { ...left.properties };
        for (const [key, rightProperty] of Object.entries(right.properties)) {
            properties[key] = Object.hasOwn(properties, key)
                ? mergeSchemaNodes(properties[key], rightProperty)
                : rightProperty;
        }
        return { kind: 'object', properties };
    }

    if (left.kind === 'array') {
        if (!left.hasItem) return right;
        if (!right.hasItem) return left;
        return {
            kind: 'array',
            hasItem: true,
            item: mergeSchemaNodes(left.item, right.item),
        };
    }

    return left;
}

// Convert the intermediate schema representation into the default values displayed and stored by the block.
function materializeSchemaNode(node) {
    switch (node.kind) {
        case 'string':
            return '';
        case 'boolean':
            return false;
        case 'number':
            return 0;
        case 'unknown':
        case 'conflict':
            return null;
        case 'array':
            return node.hasItem ? [materializeSchemaNode(node.item)] : [];
        case 'object':
            return Object.entries(node.properties).reduce((result, [key, propertyNode]) => {
                result[key] = materializeSchemaNode(propertyNode);
                return result;
            }, {});
        default:
            return null;
    }
}

// Schema processing: create default schema values without modifying the original sample JSON.
export function sanitizeSchema(value) {
    return materializeSchemaNode(toSchemaNode(value));
}

/**
 * Open workspace: read all schemas from the saved Blockly properties and return the map assigned
 * to workspace.blockSchemas before Blockly restores the individual blocks from XML.
 */
export function loadBlockSchemas(properties) {
    const storedSchemas = properties?.blockSchemas;
    if (!storedSchemas || typeof storedSchemas !== 'object' || Array.isArray(storedSchemas)) return {};

    return Object.entries(storedSchemas).reduce((schemas, [reference, entry]) => {
        if (entry?.version === BLOCK_SCHEMA_VERSION && Object.hasOwn(entry, 'schema')) {
            schemas[reference] = {
                version: BLOCK_SCHEMA_VERSION,
                schema: sanitizeSchema(entry.schema),
            };
        }
        return schemas;
    }, {});
}

/**
 * Restore each block: use the schema reference from the block XML to find that block's schema
 * in workspace.blockSchemas.
 * Schemas are sanitized when loaded or stored, so this function only performs validation and lookup.
 * `found` is returned separately because null can itself be a valid sanitized schema.
 */
export function resolveBlockSchema(workspace, reference) {
    const entry = reference ? workspace?.blockSchemas?.[reference] : null;
    if (entry?.version !== BLOCK_SCHEMA_VERSION || !Object.hasOwn(entry, 'schema')) {
        return { found: false, schema: null };
    }

    return {
        found: true,
        schema: entry.schema,
    };
}

/**
 * Apply Set/Update Schema: sanitize the dialog input and store it in workspace.blockSchemas
 * under the current block ID, making it available before the workspace is saved.
 * The sanitized value is returned so the block and stored metadata use the same structure immediately.
 */
export function storeBlockSchema(workspace, reference, schema) {
    const sanitizedSchema = sanitizeSchema(schema);
    if (!workspace || !reference) return sanitizedSchema;
    if (!workspace.blockSchemas || typeof workspace.blockSchemas !== 'object' || Array.isArray(workspace.blockSchemas)) {
        workspace.blockSchemas = {};
    }

    workspace.blockSchemas[reference] = {
        version: BLOCK_SCHEMA_VERSION,
        schema: sanitizedSchema,
    };
    return sanitizedSchema;
}

/**
 * Save workspace: return schemas from all active V3 blocks. Workspace adds this map to the
 * blockSchemas property sent to the parent application. Rebuilding the map excludes deleted blocks.
 */
export function collectBlockSchemas(workspace) {
    if (!workspace || typeof workspace.getAllBlocks !== 'function') return {};

    return workspace.getAllBlocks().reduce((schemas, block) => {
        if (block.type === FROM_JSON_WITH_SCHEMA_BLOCK_TYPE && block.hasSchema === true && block.id) {
            schemas[block.id] = {
                version: BLOCK_SCHEMA_VERSION,
                // Loading and applying already sanitize this value; reuse it instead of recursively processing it again on save.
                schema: block.jsonStructure,
            };
        }
        return schemas;
    }, {});
}
